/**
 * Unified Document Analyzer Hook
 *
 * Provides a single, consistent interface for document analysis across all modules:
 * - Canvas Document Panel
 * - Discovery Workflow
 * - AI Coach Sidebar
 * - Sources Module
 *
 * Uses the facet-first pipeline (multiPassAnalyzer) for:
 * - Evidence-based facet extraction
 * - Visual analysis for PDFs
 * - Facet → Entity mapping support
 *
 * @example
 * ```tsx
 * const { analyzeDocument, progress, facets } = useDocumentAnalyzer({
 *   onFacetsExtracted: (facets) => console.log('Found:', facets.length),
 * })
 *
 * // Analyze a file
 * await analyzeDocument({ file, contentType: 'user_interview' })
 *
 * // Or analyze pasted text
 * await analyzeDocument({ content: 'Meeting notes...', contentType: 'team_meeting' })
 * ```
 */

import { useState, useCallback, useRef, useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { sourcesApi, aiAnalysisApi, type Source, type SourceWithFacets, type DetectedFacet, type ContentType } from '@/lib/api/client'
import { getCurrentAccountId, getCurrentProjectId } from '@/lib/api/sparql-adapter'
import { useSettingsStore } from '@/stores/settingsStore'
import { useProjectStore } from '@/stores/projectStore'
import { analyzeMultiPass, type MultiPassAnalysisResult } from '@/lib/analyzer'
import { extractTextFromPdf, renderPdfToImages, type PdfProgressCallback } from '@/lib/pdf/extractor'

// ============================================================================
// TYPES
// ============================================================================

export type AnalysisStage =
  | 'idle'
  | 'uploading'
  | 'extracting_text'
  | 'rendering_images'
  | 'analyzing_text'
  | 'analyzing_visual'
  | 'deduplicating'
  | 'saving'
  | 'complete'
  | 'error'

/**
 * Enhanced progress tracking with step counts, percentages, and statistics
 */
export interface AnalysisProgress {
  // Current stage
  stage: AnalysisStage
  message: string

  // Step tracking (e.g., "Step 2 of 5")
  currentStep: number
  totalSteps: number

  // Overall percentage (0-100)
  percentage: number

  // Sub-progress within current step (for multi-page operations)
  subProgress?: {
    current: number
    total: number
    label: string // e.g., "Page 3 of 8"
  }

  // Content statistics
  stats: {
    wordCount: number
    estimatedTokens: number
    pageCount: number
  }

  // Results (updated as found)
  facetsFound: number
  visualFacetsFound: number
  visualChunksDetected: number

  // Timing
  elapsedMs: number
  estimatedRemainingMs?: number

  // Error
  error?: string
}

export interface AnalyzeDocumentInput {
  /** File to analyze (PDF, TXT, MD) */
  file?: File
  /** Raw content to analyze (alternative to file) */
  content?: string
  /** Document title */
  title?: string
  /** Content type for analysis hints */
  contentType: ContentType
  /** Source context (where the upload originated) */
  sourceContext?: 'canvas' | 'discovery' | 'coach' | 'sources'
  /** Tags to apply to the source */
  tags?: string[]
  /** Additional metadata */
  metadata?: Record<string, unknown>
}

export interface AnalysisResult {
  source: SourceWithFacets
  facets: DetectedFacet[]
  analysisTime: number
  fromCache: boolean
}

export interface UseDocumentAnalyzerOptions {
  /** Callback when source is created */
  onSourceCreated?: (source: Source) => void
  /** Callback when facets are extracted */
  onFacetsExtracted?: (facets: DetectedFacet[], visualFacets: DetectedFacet[]) => void
  /** Callback when analysis is complete */
  onAnalysisComplete?: (result: AnalysisResult) => void
  /** Error handler */
  onError?: (error: Error) => void
  /**
   * Use backend workflow system for analysis (default: true)
   *
   * When true:
   * - Routes analysis through /api/ai/analyze endpoint
   * - Prompts are managed in Settings > AI Workflows
   * - Execution is logged for monitoring
   *
   * When false:
   * - Uses direct Claude API calls from browser (legacy mode)
   */
  useBackendWorkflow?: boolean
}

export interface UseDocumentAnalyzerReturn {
  // State
  progress: AnalysisProgress
  isAnalyzing: boolean
  source: SourceWithFacets | null
  error: Error | null
  isConfigured: boolean

  // Actions
  analyzeDocument: (input: AnalyzeDocumentInput) => Promise<AnalysisResult | null>
  analyzeExistingSource: (sourceId: string) => Promise<AnalysisResult | null>
  reset: () => void
  clearError: () => void
}

// ============================================================================
// DEFAULT STATE & CONSTANTS
// ============================================================================

const DEFAULT_PROGRESS: AnalysisProgress = {
  stage: 'idle',
  message: '',
  currentStep: 0,
  totalSteps: 0,
  percentage: 0,
  stats: {
    wordCount: 0,
    estimatedTokens: 0,
    pageCount: 0,
  },
  facetsFound: 0,
  visualFacetsFound: 0,
  visualChunksDetected: 0,
  elapsedMs: 0,
}

// Step definitions for progress tracking
const STEPS = {
  TEXT_ONLY: [
    { stage: 'extracting_text', label: 'Reading document', weight: 10 },
    { stage: 'uploading', label: 'Creating source', weight: 5 },
    { stage: 'analyzing_text', label: 'Extracting insights', weight: 80 },
    { stage: 'saving', label: 'Saving results', weight: 5 },
  ],
  WITH_PDF: [
    { stage: 'extracting_text', label: 'Extracting text', weight: 15 },
    { stage: 'uploading', label: 'Creating source', weight: 5 },
    { stage: 'rendering_images', label: 'Preparing pages', weight: 15 },
    { stage: 'analyzing_text', label: 'Extracting insights', weight: 50 },
    { stage: 'analyzing_visual', label: 'Analyzing visuals', weight: 10 },
    { stage: 'saving', label: 'Saving results', weight: 5 },
  ],
} as const

// Engaging messages for each stage
const STAGE_MESSAGES: Record<AnalysisStage, string[]> = {
  idle: ['Ready to analyze'],
  uploading: ['Creating source record...', 'Preparing document...'],
  extracting_text: [
    'Reading your document...',
    'Extracting text content...',
    'Processing document structure...',
  ],
  rendering_images: [
    'Preparing pages for visual analysis...',
    'Rendering document pages...',
    'Converting to images...',
  ],
  analyzing_text: [
    'Analyzing content with AI...',
    'Identifying key insights...',
    'Extracting evidence and findings...',
    'Looking for patterns...',
  ],
  analyzing_visual: [
    'Analyzing charts and diagrams...',
    'Examining visual elements...',
    'Processing images...',
  ],
  deduplicating: [
    'Removing duplicates...',
    'Consolidating findings...',
  ],
  saving: [
    'Saving insights...',
    'Finalizing results...',
    'Almost done...',
  ],
  complete: ['Analysis complete!', 'Done!'],
  error: ['An error occurred'],
}

function getRandomMessage(stage: AnalysisStage): string {
  const messages = STAGE_MESSAGES[stage]
  return messages[Math.floor(Math.random() * messages.length)]
}

function estimateTokens(text: string): number {
  // Rough estimate: ~4 characters per token for English
  return Math.round(text.length / 4)
}

function countWords(text: string): number {
  return text.split(/\s+/).filter(Boolean).length
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

export function useDocumentAnalyzer(
  options: UseDocumentAnalyzerOptions = {}
): UseDocumentAnalyzerReturn {
  const {
    onSourceCreated,
    onFacetsExtracted,
    onAnalysisComplete,
    onError,
    useBackendWorkflow = true, // Default to using backend workflow system
  } = options

  const { claudeApiKey } = useSettingsStore()
  const { currentProject } = useProjectStore()
  const queryClient = useQueryClient()
  const abortControllerRef = useRef<AbortController | null>(null)
  const startTimeRef = useRef<number>(0)

  // Local state
  const [progress, setProgress] = useState<AnalysisProgress>(DEFAULT_PROGRESS)
  const [source, setSource] = useState<SourceWithFacets | null>(null)
  const [error, setError] = useState<Error | null>(null)

  // Context
  const accountId = getCurrentAccountId()
  const projectId = getCurrentProjectId()
  const hasContext = !!accountId && !!projectId
  const isConfigured = !!claudeApiKey
  const visualAnalysisEnabled = currentProject?.visualAnalysisEnabled ?? false

  // Update progress helper with enhanced tracking
  const updateProgress = useCallback((
    updates: Partial<AnalysisProgress> & { stage: AnalysisStage }
  ) => {
    setProgress(prev => {
      const elapsedMs = startTimeRef.current ? Date.now() - startTimeRef.current : 0
      return {
        ...prev,
        ...updates,
        elapsedMs,
        message: updates.message || getRandomMessage(updates.stage),
      }
    })
  }, [])

  // Set error helper
  const handleError = useCallback((err: Error) => {
    setError(err)
    setProgress(prev => ({
      ...prev,
      stage: 'error',
      error: err.message,
      message: err.message,
    }))
    onError?.(err)
  }, [onError])

  // Create source mutation
  const createSourceMutation = useMutation({
    mutationFn: async (data: {
      title: string
      contentType: ContentType
      rawContent: string
      markdownContent?: string
      fileName?: string
      fileType?: 'pdf' | 'md' | 'txt'
      fileData?: string
      tags?: string[]
      metadata?: Record<string, unknown>
    }) => {
      if (!accountId || !projectId) {
        throw new Error('Missing account or project context')
      }
      return sourcesApi.create(accountId, projectId, data)
    },
    onSuccess: (source) => {
      onSourceCreated?.(source)
      queryClient.invalidateQueries({ queryKey: ['sources', 'list'] })
    },
    onError: handleError,
  })

  // Save facets mutation
  const saveFacetsMutation = useMutation({
    mutationFn: async (data: {
      sourceId: string
      facets: Array<{
        facetTypeId: string
        content: string
        evidence: string
        confidence: number
        positionStart?: number
        positionEnd?: number
        suggestedEntityType?: string
        suggestedReason?: string
      }>
      visualFacets?: Array<{
        facetTypeId: string
        title: string
        content: string
        evidence?: string
        confidence: number
        pageNumber?: number
        bboxX?: number
        bboxY?: number
        bboxWidth?: number
        bboxHeight?: number
        suggestedEntityType?: string
        suggestedReason?: string
      }>
    }) => {
      if (!accountId || !projectId) {
        throw new Error('Missing account or project context')
      }
      return sourcesApi.saveFacets(
        accountId,
        projectId,
        data.sourceId,
        data.facets,
        [], // entities - empty for facet-first
        [], // relationships - empty for facet-first
        data.visualFacets
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources'] })
    },
    onError: handleError,
  })

  // Main analysis function
  const analyzeDocument = useCallback(async (
    input: AnalyzeDocumentInput
  ): Promise<AnalysisResult | null> => {
    if (!hasContext) {
      handleError(new Error('Missing account or project context'))
      return null
    }

    if (!isConfigured) {
      handleError(new Error('Claude API key not configured. Please set it in Settings.'))
      return null
    }

    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController()
    startTimeRef.current = Date.now()

    try {
      // Determine if this is a PDF and if visual analysis is enabled
      const isPdf = input.file?.name.toLowerCase().endsWith('.pdf')
      const shouldDoVisualAnalysis = isPdf && visualAnalysisEnabled
      const steps = shouldDoVisualAnalysis ? STEPS.WITH_PDF : STEPS.TEXT_ONLY
      const totalSteps = steps.length

      // ========================================================================
      // STEP 1: Extract content from file or use provided content
      // ========================================================================
      let rawContent: string
      let markdownContent: string
      let fileType: 'pdf' | 'md' | 'txt' | undefined
      let fileData: string | undefined
      let pdfBinaryData: ArrayBuffer | undefined
      let pageCount = 0

      if (input.file) {
        const fileName = input.file.name.toLowerCase()
        fileType = isPdf ? 'pdf' : fileName.endsWith('.md') ? 'md' : 'txt'

        updateProgress({
          stage: 'extracting_text',
          currentStep: 1,
          totalSteps,
          percentage: 0,
          message: `Reading ${input.file.name}...`,
        })

        if (isPdf) {
          // Read PDF as both text and binary with progress tracking
          const arrayBuffer = await input.file.arrayBuffer()
          pdfBinaryData = arrayBuffer
          fileData = btoa(
            new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
          )

          const onExtractProgress: PdfProgressCallback = (p) => {
            updateProgress({
              stage: 'extracting_text',
              currentStep: 1,
              totalSteps,
              percentage: Math.round(p.percentage * 0.15), // 0-15% of total
              subProgress: p.totalPages > 0 ? {
                current: p.currentPage,
                total: p.totalPages,
                label: `Page ${p.currentPage} of ${p.totalPages}`,
              } : undefined,
              message: p.message,
            })
          }

          const extracted = await extractTextFromPdf(input.file, onExtractProgress)
          rawContent = extracted.fullText
          markdownContent = extracted.fullText
          pageCount = extracted.totalPages
        } else {
          rawContent = await input.file.text()
          markdownContent = rawContent
        }
      } else if (input.content) {
        rawContent = input.content
        markdownContent = input.content
      } else {
        handleError(new Error('Either file or content must be provided'))
        return null
      }

      // Calculate content stats
      const wordCount = countWords(rawContent)
      const estimatedTokens = estimateTokens(rawContent)

      // ========================================================================
      // STEP 2: Create source record
      // ========================================================================
      updateProgress({
        stage: 'uploading',
        currentStep: 2,
        totalSteps,
        percentage: isPdf ? 15 : 10,
        message: 'Creating source record...',
        stats: { wordCount, estimatedTokens, pageCount },
      })

      const title = input.title || input.file?.name || 'Pasted Content'
      const sourceRecord = await createSourceMutation.mutateAsync({
        title,
        contentType: input.contentType,
        rawContent,
        markdownContent,
        fileName: input.file?.name,
        fileType,
        fileData,
        tags: input.tags,
        metadata: {
          ...input.metadata,
          sourceContext: input.sourceContext,
        },
      })

      // ========================================================================
      // STEP 3: Prepare images for visual analysis (PDFs only, when enabled)
      // ========================================================================
      let images: Array<{
        id: string
        data: string
        mimeType: string
        pageNumber: number
        width: number
        height: number
      }> = []

      if (shouldDoVisualAnalysis && pdfBinaryData) {
        const onRenderProgress: PdfProgressCallback = (p) => {
          updateProgress({
            stage: 'rendering_images',
            currentStep: 3,
            totalSteps,
            percentage: 20 + Math.round(p.percentage * 0.15), // 20-35% of total
            subProgress: p.totalPages > 0 ? {
              current: p.currentPage,
              total: p.totalPages,
              label: `Page ${p.currentPage} of ${p.totalPages}`,
            } : undefined,
            message: p.message,
          })
        }

        try {
          const pageImages = await renderPdfToImages(pdfBinaryData, {
            scale: 1.5,
            maxPages: 10,
            onProgress: onRenderProgress,
          })
          images = pageImages.map(img => ({
            id: `page_${img.pageNumber}`,
            data: img.dataUrl.split(',')[1], // Remove data URL prefix
            mimeType: 'image/png',
            pageNumber: img.pageNumber,
            width: img.width,
            height: img.height,
          }))
          console.log(`[useDocumentAnalyzer] Rendered ${images.length} PDF pages`)
        } catch (err) {
          console.warn('[useDocumentAnalyzer] Failed to render PDF pages:', err)
          // Continue without images
        }
      }

      // ========================================================================
      // STEP 4: Run facet-first analysis
      // ========================================================================
      const hasImages = images.length > 0
      const analysisStepNum = shouldDoVisualAnalysis ? 4 : 3

      updateProgress({
        stage: 'analyzing_text',
        currentStep: analysisStepNum,
        totalSteps,
        percentage: shouldDoVisualAnalysis ? 35 : 15,
        message: hasImages
          ? `Analyzing ${wordCount.toLocaleString()} words with AI (${images.length} pages for visual analysis)...`
          : `Analyzing ${wordCount.toLocaleString()} words with AI...`,
        stats: { wordCount, estimatedTokens, pageCount },
      })

      // Choose analysis method based on flag
      let analysisResult: MultiPassAnalysisResult

      if (useBackendWorkflow) {
        // Use backend workflow system - prompts managed in Settings, execution logged
        console.log('[useDocumentAnalyzer] Using backend workflow system for analysis')
        const backendResult = await aiAnalysisApi.analyzeDocument(
          {
            sourceId: sourceRecord.id,
            content: markdownContent,
            contentType: input.contentType,
            pageImages: hasImages ? images : undefined,
            accountId,
            projectId,
          },
          claudeApiKey || undefined
        )

        // Transform backend response to match MultiPassAnalysisResult format
        analysisResult = {
          sourceId: sourceRecord.id,
          facets: backendResult.facets.map(f => ({
            ...f,
            title: f.title || '',
          })),
          visualChunks: backendResult.visualChunks.map(c => ({
            ...c,
            textContext: c.textContext || '',
          })),
          visualFacets: backendResult.visualFacets.map(vf => ({
            facetTypeId: vf.facetTypeId,
            title: vf.title || '',
            content: vf.content || '',
            evidence: vf.evidence || '',
            confidence: vf.confidence,
            imageId: vf.imageId,
            suggestedEntityType: undefined,
            suggestedReason: undefined,
            bbox: undefined,
          })),
          analysisTime: backendResult.analysisTime,
          passTimings: { pass1_facets: backendResult.analysisTime },
        }
      } else {
        // Use direct Claude API calls (legacy mode)
        console.log('[useDocumentAnalyzer] Using direct Claude API calls (legacy mode)')
        analysisResult = await analyzeMultiPass(
          { apiKey: claudeApiKey! },
          {
            sourceId: sourceRecord.id,
            content: markdownContent,
            contentType: input.contentType,
            images: hasImages ? images : undefined,
          }
        )
      }

      // Check for analysis errors
      if (analysisResult.error) {
        throw new Error(`Analysis failed: ${analysisResult.error}`)
      }

      const textFacetCount = analysisResult.facets.length
      const visualFacetCount = analysisResult.visualFacets?.length || 0
      const visualChunkCount = analysisResult.visualChunks?.length || 0

      // ========================================================================
      // STEP 5: Save facets to database
      // ========================================================================
      const saveStepNum = isPdf ? 6 : 4

      updateProgress({
        stage: 'saving',
        currentStep: saveStepNum,
        totalSteps,
        percentage: 95,
        message: `Found ${textFacetCount} insights! Saving...`,
        facetsFound: textFacetCount,
        visualFacetsFound: visualFacetCount,
        visualChunksDetected: visualChunkCount,
        stats: { wordCount, estimatedTokens, pageCount },
      })

      if (textFacetCount > 0 || visualFacetCount > 0) {
        // Transform visual facets to API format
        const transformedVisualFacets = analysisResult.visualFacets?.map(vf => ({
          facetTypeId: vf.facetTypeId,
          title: vf.title,
          content: vf.content || vf.title || '',
          evidence: vf.evidence || '',
          confidence: vf.confidence,
          pageNumber: undefined,
          bboxX: vf.bbox?.x,
          bboxY: vf.bbox?.y,
          bboxWidth: vf.bbox?.width,
          bboxHeight: vf.bbox?.height,
          suggestedEntityType: vf.suggestedEntityType,
          suggestedReason: vf.suggestedReason,
        }))

        await saveFacetsMutation.mutateAsync({
          sourceId: sourceRecord.id,
          facets: analysisResult.facets,
          visualFacets: transformedVisualFacets,
        })
      }

      // ========================================================================
      // STEP 6: Fetch complete source with facets
      // ========================================================================
      const completeSource = await sourcesApi.get(accountId!, projectId!, sourceRecord.id)
      setSource(completeSource)

      // Notify callbacks
      const textFacets = completeSource.facets.filter(f => f.sourceType !== 'visual')
      const visualFacets = completeSource.facets.filter(f => f.sourceType === 'visual')
      onFacetsExtracted?.(textFacets, visualFacets)

      const result: AnalysisResult = {
        source: completeSource,
        facets: completeSource.facets,
        analysisTime: Date.now() - startTimeRef.current,
        fromCache: false,
      }

      onAnalysisComplete?.(result)

      const totalFacets = textFacetCount + visualFacetCount
      updateProgress({
        stage: 'complete',
        currentStep: totalSteps,
        totalSteps,
        percentage: 100,
        message: `Done! Found ${totalFacets} insight${totalFacets !== 1 ? 's' : ''} in ${((Date.now() - startTimeRef.current) / 1000).toFixed(1)}s`,
        facetsFound: textFacetCount,
        visualFacetsFound: visualFacetCount,
        stats: { wordCount, estimatedTokens, pageCount },
      })

      // Auto-clear success message after 5 seconds
      setTimeout(() => {
        setProgress(prev => prev.stage === 'complete' ? { ...prev, message: '' } : prev)
      }, 5000)

      return result

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error during analysis')
      handleError(error)
      return null
    } finally {
      abortControllerRef.current = null
    }
  }, [
    hasContext,
    isConfigured,
    claudeApiKey,
    accountId,
    projectId,
    createSourceMutation,
    saveFacetsMutation,
    updateProgress,
    handleError,
    onFacetsExtracted,
    onAnalysisComplete,
    useBackendWorkflow,
  ])

  // Analyze existing source (re-analysis)
  const analyzeExistingSource = useCallback(async (
    sourceId: string
  ): Promise<AnalysisResult | null> => {
    if (!hasContext || !isConfigured || !accountId || !projectId) {
      handleError(new Error('Missing context or API key'))
      return null
    }

    startTimeRef.current = Date.now()

    try {
      // Fetch existing source
      updateProgress({
        stage: 'extracting_text',
        currentStep: 1,
        totalSteps: 4,
        percentage: 5,
        message: 'Loading source content...',
      })
      const existingSource = await sourcesApi.get(accountId, projectId, sourceId)

      const isPdf = existingSource.fileType === 'pdf'
      const totalSteps = isPdf ? 5 : 4

      // Prepare images if PDF
      let images: Array<{
        id: string
        data: string
        mimeType: string
        pageNumber: number
        width: number
        height: number
      }> = []

      if (isPdf) {
        updateProgress({
          stage: 'rendering_images',
          currentStep: 2,
          totalSteps,
          percentage: 15,
          message: 'Loading PDF for visual analysis...',
        })

        try {
          const { fileData } = await sourcesApi.getFileData(accountId, projectId, sourceId)
          if (fileData) {
            const binaryString = atob(fileData)
            const bytes = new Uint8Array(binaryString.length)
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i)
            }

            const onRenderProgress: PdfProgressCallback = (p) => {
              updateProgress({
                stage: 'rendering_images',
                currentStep: 2,
                totalSteps,
                percentage: 15 + Math.round(p.percentage * 0.15),
                subProgress: p.totalPages > 0 ? {
                  current: p.currentPage,
                  total: p.totalPages,
                  label: `Page ${p.currentPage} of ${p.totalPages}`,
                } : undefined,
                message: p.message,
              })
            }

            const pageImages = await renderPdfToImages(bytes.buffer, {
              scale: 1.5,
              maxPages: 10,
              onProgress: onRenderProgress,
            })
            images = pageImages.map(img => ({
              id: `page_${img.pageNumber}`,
              data: img.dataUrl.split(',')[1],
              mimeType: 'image/png',
              pageNumber: img.pageNumber,
              width: img.width,
              height: img.height,
            }))
          }
        } catch (err) {
          console.warn('[useDocumentAnalyzer] Failed to load PDF for re-analysis:', err)
        }
      }

      // Run analysis
      const hasImages = images.length > 0
      const wordCount = countWords(existingSource.markdownContent)

      updateProgress({
        stage: 'analyzing_text',
        currentStep: isPdf ? 3 : 2,
        totalSteps,
        percentage: isPdf ? 35 : 20,
        message: `Re-analyzing ${wordCount.toLocaleString()} words...`,
        stats: {
          wordCount,
          estimatedTokens: estimateTokens(existingSource.markdownContent),
          pageCount: images.length,
        },
      })

      // Choose analysis method based on flag
      let analysisResult: MultiPassAnalysisResult

      if (useBackendWorkflow) {
        // Use backend workflow system
        console.log('[useDocumentAnalyzer] Re-analysis using backend workflow system')
        const backendResult = await aiAnalysisApi.analyzeDocument(
          {
            sourceId,
            content: existingSource.markdownContent,
            contentType: existingSource.contentType,
            pageImages: hasImages ? images : undefined,
            accountId,
            projectId,
          },
          claudeApiKey || undefined
        )

        analysisResult = {
          sourceId,
          facets: backendResult.facets.map(f => ({
            ...f,
            title: f.title || '',
          })),
          visualChunks: backendResult.visualChunks.map(c => ({
            ...c,
            textContext: c.textContext || '',
          })),
          visualFacets: backendResult.visualFacets.map(vf => ({
            facetTypeId: vf.facetTypeId,
            title: vf.title || '',
            content: vf.content || '',
            evidence: vf.evidence || '',
            confidence: vf.confidence,
            imageId: vf.imageId,
            suggestedEntityType: undefined,
            suggestedReason: undefined,
            bbox: undefined,
          })),
          analysisTime: backendResult.analysisTime,
          passTimings: { pass1_facets: backendResult.analysisTime },
        }
      } else {
        // Use direct Claude API calls (legacy mode)
        console.log('[useDocumentAnalyzer] Re-analysis using direct Claude API calls')
        analysisResult = await analyzeMultiPass(
          { apiKey: claudeApiKey! },
          {
            sourceId,
            content: existingSource.markdownContent,
            contentType: existingSource.contentType,
            images: hasImages ? images : undefined,
          }
        )
      }

      if (analysisResult.error) {
        throw new Error(`Analysis failed: ${analysisResult.error}`)
      }

      // Save facets
      const textFacetCount = analysisResult.facets.length
      const visualFacetCount = analysisResult.visualFacets?.length || 0

      updateProgress({
        stage: 'saving',
        currentStep: isPdf ? 5 : 4,
        totalSteps,
        percentage: 90,
        message: `Found ${textFacetCount + visualFacetCount} insights! Saving...`,
        facetsFound: textFacetCount,
        visualFacetsFound: visualFacetCount,
        visualChunksDetected: analysisResult.visualChunks?.length || 0,
      })

      if (textFacetCount > 0 || visualFacetCount > 0) {
        const transformedVisualFacets = analysisResult.visualFacets?.map(vf => ({
          facetTypeId: vf.facetTypeId,
          title: vf.title,
          content: vf.content || vf.title || '',
          evidence: vf.evidence || '',
          confidence: vf.confidence,
          pageNumber: undefined,
          bboxX: vf.bbox?.x,
          bboxY: vf.bbox?.y,
          bboxWidth: vf.bbox?.width,
          bboxHeight: vf.bbox?.height,
          suggestedEntityType: vf.suggestedEntityType,
          suggestedReason: vf.suggestedReason,
        }))

        await saveFacetsMutation.mutateAsync({
          sourceId,
          facets: analysisResult.facets,
          visualFacets: transformedVisualFacets,
        })
      }

      // Fetch updated source
      const completeSource = await sourcesApi.get(accountId, projectId, sourceId)
      setSource(completeSource)

      const result: AnalysisResult = {
        source: completeSource,
        facets: completeSource.facets,
        analysisTime: Date.now() - startTimeRef.current,
        fromCache: false,
      }

      onAnalysisComplete?.(result)

      updateProgress({
        stage: 'complete',
        currentStep: totalSteps,
        totalSteps,
        percentage: 100,
        message: `Re-analysis complete! Found ${textFacetCount + visualFacetCount} insights.`,
        facetsFound: textFacetCount,
        visualFacetsFound: visualFacetCount,
      })

      return result

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error during re-analysis')
      handleError(error)
      return null
    }
  }, [
    hasContext,
    isConfigured,
    claudeApiKey,
    accountId,
    projectId,
    saveFacetsMutation,
    updateProgress,
    handleError,
    onAnalysisComplete,
    useBackendWorkflow,
  ])

  // Reset state
  const reset = useCallback(() => {
    setProgress(DEFAULT_PROGRESS)
    setSource(null)
    setError(null)
    startTimeRef.current = 0
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
  }, [])

  // Clear error
  const clearError = useCallback(() => {
    setError(null)
    setProgress(prev => prev.stage === 'error' ? DEFAULT_PROGRESS : prev)
  }, [])

  return {
    // State
    progress,
    isAnalyzing: progress.stage !== 'idle' && progress.stage !== 'complete' && progress.stage !== 'error',
    source,
    error,
    isConfigured,

    // Actions
    analyzeDocument,
    analyzeExistingSource,
    reset,
    clearError,
  }
}

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Hook to get facets grouped by category for display
 */
export function useFacetsByCategory(facets: DetectedFacet[]) {
  return useMemo(() => {
    const grouped = new Map<string, DetectedFacet[]>()

    for (const facet of facets) {
      const category = facet.facetTypeId.split('_')[0] || 'other'
      const existing = grouped.get(category) || []
      grouped.set(category, [...existing, facet])
    }

    return grouped
  }, [facets])
}
